import { useState } from "react";
import Button from "../ui/Button.jsx";
import { useCreateEvent } from "../events/useCreateEvent.js";
import { getEvents } from "../services/apiEvents.js";

function JsonEventsImporter() {
    const [jsonInput, setJsonInput] = useState("");
    const [parsedEvents, setParsedEvents] = useState([]);
    const [selectedEvents, setSelectedEvents] = useState(new Set());
    const [existingEventNames, setExistingEventNames] = useState(new Set());
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const { isCreating, createEvent } = useCreateEvent();

    const handleParse = async () => {
        try {
            const parsed = JSON.parse(jsonInput);

            if (!Array.isArray(parsed)) {
                throw new Error("JSON must be an array of events");
            }

            // Fetch all existing events to check for duplicates
            const existingEvents = await getEvents();
            const existingNames = new Set(
                existingEvents.map(event => event.name.toLowerCase().trim())
            );
            setExistingEventNames(existingNames);

            setParsedEvents(parsed);
            setSelectedEvents(new Set()); // Reset selections
            setError("");
        } catch (err) {
            setError("Invalid JSON: " + err.message);
            setParsedEvents([]);
            setSelectedEvents(new Set());
            setExistingEventNames(new Set());
        }
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        const selectedEventsArray = parsedEvents.filter((_, index) => selectedEvents.has(index));
        for (const event of selectedEventsArray) {
            createEvent(event);
        }
        setSubmitting(false);
    };

    const toggleEventSelection = (index) => {
        const newSelected = new Set(selectedEvents);
        if (newSelected.has(index)) {
            newSelected.delete(index);
        } else {
            newSelected.add(index);
        }
        setSelectedEvents(newSelected);
    };

    const selectAll = () => {
        setSelectedEvents(new Set(parsedEvents.map((_, index) => index)));
    };

    const deselectAll = () => {
        setSelectedEvents(new Set());
    };

    const handleReset = () => {
        setJsonInput("");
        setParsedEvents([]);
        setSelectedEvents(new Set());
        setExistingEventNames(new Set());
        setError("");
    };

    const isEventAlreadyStored = (eventName) => {
        return existingEventNames.has(eventName.toLowerCase().trim());
    };

    const updateEventField = (index, field, value) => {
        const updatedEvents = [...parsedEvents];
        updatedEvents[index] = {
            ...updatedEvents[index],
            [field]: value
        };
        setParsedEvents(updatedEvents);
    };

    return (
        <div className="admin-tool space-y-4">
            <div className="admin-panel-intro">
                <div>
                    <h2>Import event JSON</h2>
                    <p>Paste an array, review each event, then select the records to create.</p>
                </div>
            </div>
            <textarea
                value={jsonInput}
                onChange={(e) => setJsonInput(e.target.value)}
                placeholder='Paste your JSON array here...'
                rows={8}
                className="w-full rounded-lg border bg-gray-50 p-3 font-mono"
            />

            <div className="flex flex-wrap items-center gap-2">
                <Button size="small" onClick={handleParse}>
                    Parse JSON
                </Button>
                <Button size="small" onClick={handleReset}>
                    Reset
                </Button>

                            {parsedEvents.length > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                    <Button size="small" onClick={handleSubmit} disabled={isCreating || submitting || selectedEvents.size === 0}>
                        {submitting ? "Submitting..." : `Submit ${selectedEvents.size} Selected Events`}
                    </Button>
                    <Button size="small" onClick={selectAll} disabled={isCreating || submitting}>
                        Select All
                    </Button>
                    <Button size="small" onClick={deselectAll} disabled={isCreating || submitting}>
                        Deselect All
                    </Button>
                </div>
            )}
            </div>

            {error && (
                <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">{error}</p>
            )}

            {parsedEvents.length > 0 && (
                <div className="space-y-3">
                    <p className="text-xs font-semibold text-green-700">
                        {parsedEvents.length} events parsed. {selectedEvents.size} selected.
                    </p>
                    {parsedEvents.map((event, index) => {
                        const isDuplicate = isEventAlreadyStored(event.name);
                        return (
                        <div
                            key={index}
                            className={`relative rounded-lg border p-3 transition-all ${
                                selectedEvents.has(index) ? 'ring-2 ring-blue-400 bg-blue-50' : 'bg-white'
                            } ${isDuplicate ? 'border-yellow-500 border-2' : ''}`}
                        >
                            <div className="flex items-start gap-3">
                                <input
                                    type="checkbox"
                                    checked={selectedEvents.has(index)}
                                    onChange={() => toggleEventSelection(index)}
                                    className="mt-2"
                                />
                                <div className="min-w-0 flex-1 space-y-2">
                                    <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold">
                                        Event #{index + 1}
                                        {isDuplicate && (
                                            <span className="text-yellow-600" title="This event name already exists in the database">
                                                ⚠️ Already stored
                                            </span>
                                        )}
                                    </h3>

                                    {Object.keys(event).map((key) => (
                                        <div key={key} className="grid grid-cols-1 gap-1 sm:grid-cols-4 sm:items-start sm:gap-2">
                                            <label className="pt-2 text-xs font-semibold text-gray-600">
                                                {key}:
                                            </label>
                                            {typeof event[key] === 'object' && event[key] !== null ? (
                                                <textarea
                                                    value={JSON.stringify(event[key], null, 2)}
                                                    onChange={(e) => {
                                                        try {
                                                            const parsed = JSON.parse(e.target.value);
                                                            updateEventField(index, key, parsed);
                                                        } catch (err) {
                                                            // Allow editing even if invalid JSON
                                                            updateEventField(index, key, e.target.value);
                                                        }
                                                    }}
                                                    className="col-span-3 p-2 border rounded font-mono text-sm"
                                                    rows={3}
                                                />
                                            ) : (
                                                <input
                                                    type="text"
                                                    value={event[key] ?? ''}
                                                    onChange={(e) => updateEventField(index, key, e.target.value)}
                                                    className="col-span-3 p-2 border rounded"
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default JsonEventsImporter;
